
import { Link } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';

export default function Home() {
  const { t } = useTranslation();
  
  useEffect(() => {
    // Update page title and meta description
    document.title = 'Lions Trade Expert -  Commodity Trade';
    
    // Add JSON-LD structured data
    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Organization",
      "name": "Lions Trade Expert",
      "description": "Informative portal about sugar and coffee exports from Brazil to the world. Market analysis, logistics and trends in Brazilian agribusiness.",
      "url": `${import.meta.env.VITE_SITE_URL || "https://example.com"}`,
      "logo": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/logo.png`,
      "address": {
        "@type": "PostalAddress",
        "addressCountry": "BR"
      },
      "areaServed": "BR",
      "knowsAbout": ["export", "commodities", "sugar", "coffee", "Brazil", "agribusiness", "logistics", "ports"],
      "sameAs": [
        "https://twitter.com/lionstradeexpert",
        "https://linkedin.com/company/lionstradeexpert"
      ],
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/`
      },
      "potentialAction": {
        "@type": "SearchAction",
        "target": `${import.meta.env.VITE_SITE_URL || "https://example.com"}/noticias?q={search_term_string}`,
        "query-input": "required name=search_term_string"
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const features = [
    {
      title: t('features.sugar.title'),
      description: t('features.sugar.description'),
      icon: 'ri-plant-line',
      link: '/acucar',
      color: 'bg-gradient-to-br from-blue-500 to-blue-600'
    },
    {
      title: t('features.coffee.title'),
      description: t('features.coffee.description'),
      icon: 'ri-cup-line',
      link: '/cafe',
      color: 'bg-gradient-to-br from-amber-500 to-amber-600'
    },
    {
      title: t('features.logistics.title'),
      description: t('features.logistics.description'),
      icon: 'ri-ship-line',
      link: '/logistica',
      color: 'bg-gradient-to-br from-cyan-500 to-cyan-600'
    }
  ];

  const stats = [
    { value: '60%', label: t('stats.sugarExport'), icon: 'ri-plant-line' },
    { value: '40%', label: t('stats.coffeeWorld'), icon: 'ri-cup-line' },
    { value: '4', label: t('stats.mainPorts'), icon: 'ri-ship-line' },
    { value: '100+', label: t('stats.countries'), icon: 'ri-global-line' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section - Mobile Optimized */}
      <section 
        className="relative min-h-screen flex items-center justify-center text-white overflow-hidden"
        style={{
          backgroundImage: `linear-gradient(135deg, rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.4)), url('https://public.readdy.ai/ai/img_res/cc0029b3e586857dd3644cbf0a4ee6a4.jpg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'scroll'
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="mb-6 sm:mb-8">
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 bg-gradient-to-r from-white to-amber-200 bg-clip-text text-transparent leading-tight">
              {t('hero.title')}
            </h1>
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light mb-6 sm:mb-8 text-amber-300 leading-tight">
              {t('hero.subtitle')}
            </h2>
          </div>
          <div className="backdrop-blur-sm bg-white/10 rounded-2xl p-4 sm:p-6 lg:p-8 mb-8 sm:mb-12 border border-white/20">
            <p className="text-lg sm:text-xl md:text-2xl mb-6 sm:mb-8 max-w-4xl mx-auto leading-relaxed">
              {t('hero.description')}
            </p>
          </div>
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-6 justify-center">
            <Link to="/acucar" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 border-none shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap min-h-[48px] text-base sm:text-lg px-6 sm:px-8">
                <i className="ri-plant-line mr-2"></i>
                {t('hero.exploreSugar')}
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
            <Link to="/cafe" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto bg-white/10 border-2 border-white text-white hover:bg-white hover:text-gray-900 backdrop-blur-sm shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap min-h-[48px] text-base sm:text-lg px-6 sm:px-8">
                <i className="ri-cup-line mr-2"></i>
                {t('hero.discoverCoffee')}
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <i className="ri-arrow-down-line text-white text-xl sm:text-2xl"></i>
        </div>
      </section>

      {/* Features Section - Single Column on Mobile */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              {t('features.title')}
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto mb-4 sm:mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('features.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 sm:gap-8 lg:gap-10">
            {features.map((feature, index) => (
              <div key={index} className="group bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2">
                <div className="p-6 sm:p-8 lg:p-10">
                  <div className={`w-16 h-16 sm:w-20 sm:h-20 ${feature.color} rounded-2xl flex items-center justify-center mb-6 sm:mb-8 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    <i className={`${feature.icon} text-white text-2xl sm:text-3xl`}></i>
                  </div>
                  <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 group-hover:text-amber-600 transition-colors">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600 mb-6 sm:mb-8 leading-relaxed text-base sm:text-lg">
                    {feature.description}
                  </p>
                  <Link to={feature.link}>
                    <Button variant="outline" className="w-full group-hover:bg-amber-500 group-hover:border-amber-500 group-hover:text-white transition-all duration-300 whitespace-nowrap min-h-[44px] text-base">
                      {t('features.learnMore')}
                      <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform"></i>
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section - 2x2 Grid on Mobile */}
      <section className="py-12 sm:py-16 lg:py-24 bg-gradient-to-r from-black via-gray-900 to-black text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 sm:mb-16 lg:mb-20">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 sm:mb-6">
              {t('stats.title')}
            </h2>
            <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto mb-4 sm:mb-6"></div>
            <p className="text-lg sm:text-xl text-gray-300">
              {t('stats.description')}
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center group">
                <div className="bg-gradient-to-br from-amber-500 to-orange-500 w-16 h-16 sm:w-20 sm:h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 group-hover:scale-110 transition-transform duration-300 shadow-xl">
                  <i className={`${stat.icon} text-white text-xl sm:text-2xl`}></i>
                </div>
                <div className="text-3xl sm:text-4xl lg:text-6xl font-bold mb-2 sm:mb-4 bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text text-transparent">{stat.value}</div>
                <div className="text-gray-300 text-sm sm:text-base lg:text-lg leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Mobile Optimized */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white relative">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6 sm:mb-8">
            {t('cta.title')}
          </h2>
          <div className="w-16 sm:w-24 h-1 bg-gradient-to-r from-amber-400 to-orange-500 mx-auto mb-6 sm:mb-8"></div>
          <p className="text-lg sm:text-xl text-gray-600 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
            {t('cta.description')}
          </p>
          <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-6 justify-center">
            <Link to="/noticias" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap min-h-[48px] text-base sm:text-lg px-6 sm:px-8">
                <i className="ri-news-line mr-2"></i>
                {t('cta.seeNews')}
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
            <Link to="/contato" className="w-full sm:w-auto">
              <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-gray-900 hover:bg-gray-900 hover:text-white shadow-xl transform hover:scale-105 transition-all duration-300 whitespace-nowrap min-h-[48px] text-base sm:text-lg px-6 sm:px-8">
                <i className="ri-mail-line mr-2"></i>
                {t('cta.contact')}
                <i className="ri-arrow-right-line ml-2"></i>
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
